/**
 * The input agent: turns pointers (mouse, touch, pen) into board commands.
 *
 * Touch zones (docs/04-build-ux-decisions.md):
 *  - a silver SNAP   → draw a new wire from it (or, on a wire's end, stretch that wire)
 *  - a part's BODY   → drag to move · tap = menu / flip switch · hold = press button · long-press = info
 *  - EMPTY space     → drag to pan · pinch or scroll to zoom · double-tap to re-fit
 *  - drop a part on the TRAY to remove it
 */
import { footprint, type PlaceProblem } from '../core/board';
import type { Board } from '../core/board';
import { connectScore } from '../core/spot';
import type { Command } from '../core/commands';
import type { PartProps, PartType, Placement, Rot } from '../core/types';
import { DIRS } from '../core/types';
import { WIRE_MAX } from '../parts/defs';
import { partDef } from '../parts/registry';
import type { Stage } from '../render/stage';

export type Feedback = 'pickup' | 'invalid' | 'poof' | 'stuck' | 'drop' | 'tick' | 'draw';

export interface ControllerHost {
  readonly board: Board;
  readonly stage: Stage;
  /** Run a command through history. Returns false (and gives feedback) if it was refused. */
  run(cmd: Command, opts?: { snapped?: string }): boolean;
  toggle(id: string): void;
  press(id: string, down: boolean): void;
  select(id: string | null): void;
  info(id: string): void;
  feedback(kind: Feedback, problem?: PlaceProblem | 'stuck'): void;
  /** Can this part be moved/changed (levels lock some parts)? `quiet` = don't explain why not. */
  canEdit(id: string, quiet?: boolean): boolean;
  /** Is the pointer over the tray (drop here to remove)? */
  overTray(x: number, y: number): boolean;
  /** Show/hide the tray's "drop here to remove" state. */
  trashMode(on: boolean, hot?: boolean): void;
  /** Color for the next drawn wire. */
  nextWireColor(): string;
  /** Called when the kid draws a wire for the first time, to celebrate/teach. */
  onWireDrawn?(): void;
}

const DRAG_THRESHOLD = 7;
const LONG_PRESS_MS = 480;
/** On fixed boards, how far (in studs) outside still counts as "over the board". */
const BOARD_MARGIN = 0.9;

interface Drag {
  type: PartType;
  props: PartProps;
  rot: Rot;
  /** Existing part being moved (null = new from the tray). */
  id: string | null;
  pointerId: number;
  touch: boolean;
  trash: boolean;
  /** The kid turned it themselves (R / second finger): stop auto-turning. */
  manualRot: boolean;
  candidate: {
    x: number;
    y: number;
    rot: Rot;
    ok: boolean;
    layer: number;
    problem?: PlaceProblem;
    onBoard: boolean;
  } | null;
}

interface Draw {
  pointerId: number;
  touch: boolean;
  sx: number;
  sy: number;
  color: string;
  /** Wire being stretched (hidden while drawing; replaced on success). */
  stretching: string | null;
  /** Part whose snap we started on (a tap without moving selects it instead). */
  fromId: string;
  moved: boolean;
  x0: number;
  y0: number;
  last: string;
  candidate: { part: Placement; ok: boolean; problem?: PlaceProblem } | null;
}

interface Press {
  id: string;
  pointerId: number;
  x: number;
  y: number;
  timer: number;
  held: boolean;
  longFired: boolean;
}

interface Pan {
  pointerId: number;
  x: number;
  y: number;
  moved: boolean;
}

export class Controller {
  private drag: Drag | null = null;
  private draw: Draw | null = null;
  private press: Press | null = null;
  private pan: Pan | null = null;
  private touches = new Map<number, { x: number; y: number }>();
  private pinch: { dist: number; mx: number; my: number } | null = null;
  private lastPointer = { x: 0, y: 0 };
  private lastTap = 0;
  enabled = true;

  constructor(private host: ControllerHost) {
    const canvas = host.stage.canvas;
    canvas.addEventListener('pointerdown', (e) => this.onDown(e));
    window.addEventListener('pointermove', (e) => this.onMove(e), { passive: false });
    window.addEventListener('pointerup', (e) => this.onUp(e));
    window.addEventListener('pointercancel', (e) => this.onUp(e, true));
    canvas.addEventListener(
      'wheel',
      (e) => {
        e.preventDefault();
        host.stage.zoomBy(Math.exp(-e.deltaY * 0.0015));
      },
      { passive: false },
    );
    window.addEventListener('keydown', (e) => {
      if ((e.key === 'r' || e.key === 'R') && this.drag) {
        this.rotateDrag();
        e.preventDefault();
      }
      if (e.key === 'Escape') this.cancelAll();
    });
    canvas.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  get dragging() {
    return !!this.drag || !!this.draw;
  }

  /** Start dragging a brand-new part out of the tray. */
  beginNew(type: PartType, props: PartProps, e: PointerEvent) {
    if (!this.enabled) return;
    this.cancelPress();
    this.drag = {
      type,
      props: { ...partDef(type).defaultProps(), ...props },
      rot: 0,
      id: null,
      pointerId: e.pointerId,
      touch: e.pointerType === 'touch',
      trash: false,
      manualRot: false,
      candidate: null,
    };
    this.host.select(null);
    this.host.feedback('pickup');
    this.updateDrag(e.clientX, e.clientY);
  }

  rotateDrag() {
    if (!this.drag) return;
    this.drag.rot = (((this.drag.candidate?.rot ?? this.drag.rot) + 1) % 4) as Rot;
    this.drag.manualRot = true;
    this.updateDrag(this.lastPointer.x, this.lastPointer.y);
  }

  // ───────────────────────────── Pointer routing ─────────────────────────────
  private onDown(e: PointerEvent) {
    if (!this.enabled) return;
    if (e.pointerType === 'touch') this.touches.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (this.drag) {
      // A second finger while dragging a part rotates it.
      if (e.pointerId !== this.drag.pointerId) this.rotateDrag();
      return;
    }
    if (this.draw) return;

    // Two fingers on the board = pinch-zoom / pan.
    if (this.touches.size === 2) {
      this.cancelPress();
      this.pan = null;
      this.pinch = this.pinchState();
      return;
    }

    // Right/middle mouse button pans anywhere.
    if (e.pointerType === 'mouse' && e.button !== 0) {
      this.pan = { pointerId: e.pointerId, x: e.clientX, y: e.clientY, moved: true };
      return;
    }

    const hit = this.host.stage.pick(e.clientX, e.clientY);
    if (!hit) {
      this.pan = { pointerId: e.pointerId, x: e.clientX, y: e.clientY, moved: false };
      return;
    }
    const p = this.host.board.parts.get(hit.id);
    if (!p) return;
    e.preventDefault();

    if (hit.pin !== null) {
      this.beginDraw(p, hit.pin, e);
      return;
    }

    const def = partDef(p.type);
    const press: Press = {
      id: hit.id,
      pointerId: e.pointerId,
      x: e.clientX,
      y: e.clientY,
      timer: 0,
      held: false,
      longFired: false,
    };
    if (def.interaction === 'hold') {
      press.held = true;
      this.host.press(hit.id, true);
    }
    press.timer = window.setTimeout(() => {
      if (this.press !== press) return;
      press.longFired = true;
      if (press.held) {
        this.host.press(press.id, false);
        press.held = false;
      }
      this.host.select(press.id);
      this.host.info(press.id);
    }, LONG_PRESS_MS);
    this.press = press;
  }

  private onMove(e: PointerEvent) {
    this.lastPointer = { x: e.clientX, y: e.clientY };
    if (this.touches.has(e.pointerId)) this.touches.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (this.pinch && this.touches.size >= 2) {
      e.preventDefault();
      const now = this.pinchState();
      if (now.dist > 0 && this.pinch.dist > 0) this.host.stage.zoomBy(now.dist / this.pinch.dist);
      this.host.stage.panBy(now.mx - this.pinch.mx, now.my - this.pinch.my);
      this.pinch = now;
      return;
    }
    if (this.press && e.pointerId === this.press.pointerId && !this.press.longFired) {
      if (Math.hypot(e.clientX - this.press.x, e.clientY - this.press.y) > DRAG_THRESHOLD)
        this.startMove(this.press, e);
    }
    if (this.drag && e.pointerId === this.drag.pointerId) {
      e.preventDefault();
      this.updateDrag(e.clientX, e.clientY);
    }
    if (this.draw && e.pointerId === this.draw.pointerId) {
      e.preventDefault();
      if (
        !this.draw.moved &&
        Math.hypot(e.clientX - this.draw.x0, e.clientY - this.draw.y0) > DRAG_THRESHOLD
      ) {
        this.draw.moved = true;
        this.startDrawing();
      }
      if (this.draw.moved) this.updateDraw(e.clientX, e.clientY);
    }
    if (this.pan && e.pointerId === this.pan.pointerId) {
      const dx = e.clientX - this.pan.x;
      const dy = e.clientY - this.pan.y;
      if (!this.pan.moved && Math.hypot(dx, dy) > DRAG_THRESHOLD) this.pan.moved = true;
      if (this.pan.moved) {
        this.host.stage.panBy(dx, dy);
        this.pan.x = e.clientX;
        this.pan.y = e.clientY;
      }
    }
  }

  private onUp(e: PointerEvent, cancelled = false) {
    this.touches.delete(e.pointerId);
    if (this.pinch) {
      if (this.touches.size < 2) this.pinch = null;
      return;
    }
    if (this.press && e.pointerId === this.press.pointerId) {
      const press = this.press;
      this.cancelPress();
      if (press.held) this.host.press(press.id, false);
      if (!press.longFired && !cancelled) this.tap(press.id);
      return;
    }
    if (this.drag && e.pointerId === this.drag.pointerId) this.finishDrag(cancelled);
    if (this.draw && e.pointerId === this.draw.pointerId) this.finishDraw(cancelled);
    if (this.pan && e.pointerId === this.pan.pointerId) {
      const wasTap = !this.pan.moved;
      this.pan = null;
      if (wasTap && !cancelled) {
        this.host.select(null);
        const now = performance.now();
        if (now - this.lastTap < 320) this.host.stage.resetView();
        this.lastTap = now;
      }
    }
  }

  private pinchState() {
    const [a, b] = [...this.touches.values()];
    if (!a || !b) return { dist: 0, mx: 0, my: 0 };
    return { dist: Math.hypot(a.x - b.x, a.y - b.y), mx: (a.x + b.x) / 2, my: (a.y + b.y) / 2 };
  }

  private cancelAll() {
    if (this.drag) this.finishDrag(true);
    if (this.draw) this.finishDraw(true);
    this.cancelPress();
    this.pan = null;
  }

  private tap(id: string) {
    const p = this.host.board.parts.get(id);
    if (!p) return;
    const def = partDef(p.type);
    if (def.interaction === 'toggle') this.host.toggle(id);
    else if (def.interaction === 'hold') return;
    else this.host.select(this.host.stage.selectedId === id ? null : id);
  }

  private cancelPress() {
    if (this.press) window.clearTimeout(this.press.timer);
    this.press = null;
  }

  // ───────────────────────────── Moving parts ─────────────────────────────
  private startMove(press: Press, e: PointerEvent) {
    const p = this.host.board.parts.get(press.id);
    this.cancelPress();
    if (press.held) this.host.press(press.id, false);
    if (!p) return;
    if (!this.host.canEdit(p.id)) {
      this.host.feedback('stuck');
      return;
    }
    if (this.host.board.stackedOn(p.id).length) {
      this.host.feedback('stuck', 'stuck');
      return;
    }
    this.drag = {
      type: p.type,
      props: { ...p.props },
      rot: p.rot,
      id: p.id,
      pointerId: press.pointerId,
      touch: e.pointerType === 'touch',
      trash: false,
      manualRot: false,
      candidate: null,
    };
    this.host.select(null);
    this.host.stage.setHidden(p.id);
    this.host.trashMode(true);
    this.host.feedback('pickup');
    this.updateDrag(e.clientX, e.clientY);
  }

  /** Recompute where the dragged part would land. */
  private updateDrag(clientX: number, clientY: number) {
    const d = this.drag;
    if (!d) return;
    const stage = this.host.stage;
    const board = this.host.board;
    if (d.id) {
      d.trash = this.host.overTray(clientX, clientY);
      this.host.trashMode(true, d.trash);
    }
    // Fingers hide what they hold: aim a little above the touch point.
    const aimY = d.touch ? clientY - 46 : clientY;
    const gp = stage.gridPoint(clientX, aimY, 0);
    if (!gp) return;
    const reach = board.elastic ? 6 : BOARD_MARGIN;
    const onBoard =
      !d.trash &&
      gp.gx > -reach &&
      gp.gy > -reach &&
      gp.gx < board.cols - 1 + reach &&
      gp.gy < board.rows - 1 + reach;
    const at = (rot: Rot) => {
      const len = d.type === 'jumper' ? 1 : partDef(d.type).length(d.props);
      const mid = Math.floor((len - 1) / 2);
      const dir = DIRS[rot]!;
      // Hold the part by its middle stud; for even lengths, pick the nearer end.
      const cx = Math.round(gp.gx - (len % 2 === 0 ? dir.x * 0.5 : 0));
      const cy = Math.round(gp.gy - (len % 2 === 0 ? dir.y * 0.5 : 0));
      const cand: Placement = {
        id: d.id ?? '__new',
        type: d.type,
        x: cx - dir.x * mid,
        y: cy - dir.y * mid,
        rot,
        layer: 0,
        props: d.props,
      };
      return { cand, chk: board.check(cand, d.id ?? undefined) };
    };
    // Auto-turn new parts to face whichever way snaps onto the most nearby snaps,
    // so a motor dropped into an up-down gap stands up by itself.
    let best = at(d.rot);
    if (!d.id && !d.manualRot && d.type !== 'jumper') {
      let bestScore = this.connects(best.cand, best.chk.ok);
      for (const r of [0, 1, 2, 3] as Rot[]) {
        if (r === d.rot) continue;
        const t = at(r);
        const sc = this.connects(t.cand, t.chk.ok);
        if (sc > bestScore) {
          best = t;
          bestScore = sc;
        }
      }
    }
    const { cand, chk } = best;
    d.candidate = {
      x: cand.x,
      y: cand.y,
      rot: cand.rot,
      ok: chk.ok,
      layer: chk.layer,
      problem: chk.problem,
      onBoard,
    };
    const cells = onBoard ? footprint(cand).filter((c) => board.reachable(c)) : null;
    stage.showGhost(cand, gp.world.clone(), cells, chk.ok, chk.layer);
  }

  /** How well a candidate connects to nearby snaps (−99 if it can't go there). */
  private connects(p: Placement, ok: boolean): number {
    if (!ok) return -99;
    const layer = this.host.board.check(p, p.id).layer;
    return connectScore(this.host.board, p, layer, p.id);
  }

  private finishDrag(cancelled: boolean) {
    const d = this.drag!;
    this.drag = null;
    const stage = this.host.stage;
    stage.hideGhost();
    stage.setHidden(null);
    this.host.trashMode(false);
    const c = d.candidate;

    if (cancelled || !c) return;
    if (d.trash || !c.onBoard) {
      if (d.id) {
        this.host.run({ op: 'remove', id: d.id });
        this.host.feedback('poof');
      } else this.host.feedback('drop');
      return;
    }
    if (!c.ok) {
      this.host.feedback('invalid', c.problem);
      return;
    }
    if (d.id) {
      const p = this.host.board.parts.get(d.id);
      if (p && p.x === c.x && p.y === c.y && p.rot === c.rot) {
        stage.bump(d.id);
        return;
      }
      this.host.run({ op: 'move', id: d.id, x: c.x, y: c.y, rot: c.rot }, { snapped: d.id });
    } else {
      const id = this.host.board.newId();
      this.host.run(
        { op: 'place', part: { id, type: d.type, x: c.x, y: c.y, rot: c.rot, layer: 0, props: d.props } },
        { snapped: id },
      );
    }
  }

  // ───────────────────────────── Drawing & stretching wires ─────────────────────────────
  private beginDraw(p: Placement, pin: number, e: PointerEvent) {
    const cells = footprint(p).filter((c) => c.pin >= 0);
    const here = cells.find((c) => c.pin === pin) ?? cells[0]!;
    const isWire = p.type === 'wire' || p.type === 'jumper';
    // Grabbing a wire's end stretches that wire: we draw from its OTHER end.
    const stretch = isWire && this.host.canEdit(p.id, true) && !this.host.board.stackedOn(p.id).length;
    const from = stretch ? (cells.find((c) => c.pin !== pin) ?? here) : here;
    this.draw = {
      pointerId: e.pointerId,
      touch: e.pointerType === 'touch',
      sx: from.x,
      sy: from.y,
      color: stretch ? String(p.props.color ?? this.host.nextWireColor()) : this.host.nextWireColor(),
      stretching: stretch ? p.id : null,
      fromId: p.id,
      moved: false,
      x0: e.clientX,
      y0: e.clientY,
      last: '',
      candidate: null,
    };
  }

  private startDrawing() {
    const d = this.draw!;
    this.host.select(null);
    if (d.stretching) this.host.stage.setHidden(d.stretching);
    // Light up every snap you could connect to.
    const board = this.host.board;
    const targets: { x: number; y: number; layer: number }[] = [];
    const seen = new Set<string>();
    for (const p of board.parts.values()) {
      if (p.id === d.stretching) continue;
      for (const c of footprint(p)) {
        if (c.pin < 0) continue;
        const k = `${c.x},${c.y}`;
        if (seen.has(k) || (c.x === d.sx && c.y === d.sy)) continue;
        const stack = board.stackAt(c.x, c.y, d.stretching ?? undefined);
        const top = stack[stack.length - 1];
        if (!top || top.cell.pin < 0) continue;
        seen.add(k);
        targets.push({ x: c.x, y: c.y, layer: top.part.layer });
      }
    }
    this.host.stage.showSnapTargets(targets);
    this.host.feedback('draw');
  }

  /** Nearest stud to the pointer, pulled toward snaps ("magnetic"). */
  private aimCell(clientX: number, clientY: number, touch: boolean) {
    const gp = this.host.stage.gridPoint(clientX, touch ? clientY - 30 : clientY, 0);
    if (!gp) return null;
    let x = Math.round(gp.gx);
    let y = Math.round(gp.gy);
    // Magnet: a snap within reach wins over a plain stud.
    let best = 0.75;
    const board = this.host.board;
    for (const p of board.parts.values()) {
      if (p.id === this.draw?.stretching) continue;
      for (const c of footprint(p)) {
        if (c.pin < 0) continue;
        const dist = Math.hypot(c.x - gp.gx, c.y - gp.gy);
        if (dist < best) {
          best = dist;
          x = c.x;
          y = c.y;
        }
      }
    }
    return { x, y };
  }

  private updateDraw(clientX: number, clientY: number) {
    const d = this.draw!;
    const board = this.host.board;
    const aim = this.aimCell(clientX, clientY, d.touch);
    if (!aim) return;
    const key = `${aim.x},${aim.y}`;
    if (key !== d.last) {
      d.last = key;
      this.host.feedback('tick');
    }
    const dx = aim.x - d.sx;
    const dy = aim.y - d.sy;
    if (!dx && !dy) {
      d.candidate = null;
      this.host.stage.hideGhost();
      return;
    }
    const exclude = d.stretching ?? undefined;
    const candidates: Placement[] = [];
    const straight = (dx === 0 || dy === 0) && Math.abs(dx + dy) + 1 <= WIRE_MAX;
    if (straight) {
      const rot: Rot = dx > 0 ? 0 : dy > 0 ? 1 : dx < 0 ? 2 : 3;
      candidates.push({
        id: '__draw',
        type: 'wire',
        x: d.sx,
        y: d.sy,
        rot,
        layer: 0,
        props: { length: Math.abs(dx + dy) + 1, color: d.color },
      });
    }
    candidates.push({
      id: '__draw',
      type: 'jumper',
      x: d.sx,
      y: d.sy,
      rot: 0,
      layer: 0,
      props: { dx, dy, color: d.color },
    });
    let chosen = candidates[0]!;
    let chk = board.check(chosen, exclude);
    if (!chk.ok && candidates[1]) {
      const alt = board.check(candidates[1], exclude);
      if (alt.ok) {
        chosen = candidates[1];
        chk = alt;
      }
    }
    d.candidate = { part: { ...chosen, layer: chk.layer }, ok: chk.ok, problem: chk.problem };
    const cells = footprint(chosen).filter((c) => c.pin >= 0);
    this.host.stage.showGhost(chosen, null, cells, chk.ok, chk.layer, 0);
  }

  private finishDraw(cancelled: boolean) {
    const d = this.draw!;
    this.draw = null;
    const stage = this.host.stage;
    stage.hideGhost();
    stage.hideSnapTargets();
    stage.setHidden(null);
    if (cancelled) return;
    if (!d.moved) {
      // Just a tap on a snap: treat it like tapping the part.
      this.tap(d.fromId);
      return;
    }
    const c = d.candidate;
    if (!c) return;
    if (!c.ok) {
      this.host.feedback('invalid', c.problem);
      return;
    }
    const id = this.host.board.newId();
    const place: Command = { op: 'place', part: { ...c.part, id, layer: 0 } };
    const ok = d.stretching
      ? this.host.run({ op: 'batch', cmds: [{ op: 'remove', id: d.stretching }, place] }, { snapped: id })
      : this.host.run(place, { snapped: id });
    if (ok && !d.stretching) this.host.onWireDrawn?.();
  }
}
